/**
 * Realistisk Balancemarked og Systemydelser Konfiguration
 * Baseret på faktiske DK2 markedsdata 2024
 * 
 * Dokumentation for 30 MW el-kedel med 3420 disponible timer
 */

export const BALANCE_MARKET_CONFIG = {
  // Total disponible timer fra simulering
  totalAvailableHours: 3420,
  
  // El-kedel kapacitet
  heaterCapacity_MW: 30,
  
  // Fordeling af systemydelser baseret på markedsanalyse
  services: {
    /**
     * mFRR Nedregulering - Primær indtægtskilde (60% af timer)
     * Manual Frequency Restoration Reserve
     * 
     * BELÆG:
     * - Historiske data viser 14.500 kr/år for 10 kW i DK2 (2024)
     * - Dette svarer til ca. 165 kr/kW/h ved fuld udnyttelse
     * - mFRR har 12,5 minutters aktiveringstid - perfekt for el-kedler
     * - Mest stabil og forudsigelig ydelse
     * 
     * KILDER:
     * - FCR & Systemydelser markedsdata 2024
     * - Energinet.dk prisstatistikker
     */
    mFRR_ned: {
      name: 'mFRR Nedregulering',
      description: 'Manual Frequency Restoration Reserve - primær systemydelse',
      percentOfHours: 0.60,
      hours: Math.floor(3420 * 0.60), // 2052 timer
      priceRange: {
        min: 50,
        max: 150,
        conservative: 100, // DKK/MW/h
      },
      activationTime: '12,5 minutter',
      technicalRequirements: 'Standard el-kedel automation',
      marketStatus: 'Trilateralt marked siden nov. 2024',
    },
    
    /**
     * aFRR Nedregulering - Sekundær indtægtskilde (30% af timer)
     * Automatic Frequency Restoration Reserve
     * 
     * BELÆG:
     * - Fra oktober 2024: Både kapacitetsmarked (CM) og energiaktiveringsmarked (EAM)
     * - Typisk 30-50% højere priser end mFRR pga. hurtigere aktivering
     * - Kræver automatisk styring og integration
     * 
     * KILDER:
     * - DanskFjernvarme.dk markedsanalyse
     * - Montel Analytics - Danish Imbalance Prices 2024
     */
    aFRR_ned: {
      name: 'aFRR Nedregulering',
      description: 'Automatic Frequency Restoration Reserve - hurtig respons',
      percentOfHours: 0.30,
      hours: Math.floor(3420 * 0.30), // 1026 timer
      priceRange: {
        min: 100,
        max: 200,
        conservative: 150, // DKK/MW/h
      },
      activationTime: '5 minutter',
      technicalRequirements: 'Avanceret automatisk styring påkrævet',
      marketStatus: 'Nyt kapacitetsmarked fra okt. 2024',
    },
    
    /**
     * FCR-D Nedregulering - Supplement (10% af timer)
     * Frequency Containment Reserve for Disturbances
     * 
     * BELÆG:
     * - 5 kW kan tjene op til 12.251 kr årligt i DK2 (2024)
     * - Dette indikerer ca. 280 kr/kW/h ved fuld udnyttelse
     * - Højeste priser pga. hurtigste responstid
     * - Kun tilgængelig i DK2 (geografisk fordel)
     * 
     * KILDER:
     * - NordCharger netbalancering data
     * - Energinet FCR-D prisstatistik
     */
    FCR_D: {
      name: 'FCR-D Nedregulering',
      description: 'Frequency Containment Reserve - hurtigste respons',
      percentOfHours: 0.10,
      hours: Math.floor(3420 * 0.10), // 342 timer
      priceRange: {
        min: 150,
        max: 250,
        conservative: 200, // DKK/MW/h
      },
      activationTime: '30 sekunder',
      technicalRequirements: 'Meget avanceret styringssystem',
      marketStatus: 'Højeste priser i DK2',
    },
  },
  
  /**
   * Energiaktivering - Når systemet faktisk aktiveres
   * Udover kapacitetsbetaling modtages betaling for faktisk leveret energi
   */
  energyActivation: {
    expectedActivationRate: 0.10, // 10% af timer forventes aktiveret
    netProfitPerMWh: 100, // DKK/MWh efter elforbrug og tariffer
    description: 'Betaling for faktisk leveret energi ved aktivering',
  },
  
  /**
   * Markedsrisici og usikkerhed
   */
  marketRisks: {
    volatility: 'Priserne faldet markant i 2024 vs. 2023',
    competition: 'Flere batterier kommer ind på markedet',
    technicalRequirements: 'Skal bestå prækvalifikation hos Energinet',
    futureChanges: 'Nye prismekanismer fra 18. marts 2025',
    uncertaintyRange: {
      conservative: 0.70, // -30% for konservativt estimat
      optimistic: 1.30,   // +30% for optimistisk scenario
    },
  },
  
  /**
   * Geografiske fordele i DK2
   */
  geographicAdvantages: {
    region: 'DK2 (Sjælland)',
    advantages: [
      'Betydeligt højere priser end DK1',
      'Større behov for fleksibilitet pga. høj vindkraft andel',
      'FCR-D kun tilgængelig i DK2',
      'Tættere på kontinentale markeder',
    ],
  },
};

/**
 * Beregn forventet årlig indtægt fra systemydelser
 */
export const calculateBalanceMarketRevenue = (availableHours = 3420, heaterCapacity = 30) => {
  const { services, energyActivation } = BALANCE_MARKET_CONFIG;

  // Dynamisk fordeling - kun mFRR og aFRR (el-kedel kan ikke levere FCR-D med 30 sek krav)
  const mFRRHours = Math.floor(availableHours * 0.67); // 67% til mFRR
  const aFRRHours = Math.floor(availableHours * 0.33); // 33% til aFRR

  // Kapacitetsindtægter med dynamiske timer
  const capacityRevenue = {
    mFRR: mFRRHours * heaterCapacity * services.mFRR_ned.priceRange.conservative,
    aFRR: aFRRHours * heaterCapacity * services.aFRR_ned.priceRange.conservative,
    FCR_D: 0, // El-kedel kan ikke levere FCR-D (kræver 30 sek responstid)
  };
  
  const totalCapacityRevenue = Object.values(capacityRevenue).reduce((a, b) => a + b, 0);
  
  // Energiaktiveringsindtægt
  const activationHours = availableHours * energyActivation.expectedActivationRate;
  const activationMWh = activationHours * heaterCapacity;
  const energyRevenue = activationMWh * energyActivation.netProfitPerMWh;
  
  // Total indtægt
  const totalRevenue = totalCapacityRevenue + energyRevenue;
  
  return {
    capacityRevenue,
    totalCapacityRevenue,
    energyRevenue,
    totalRevenue,
    breakdown: {
      mFRR: {
        hours: mFRRHours,
        revenue: capacityRevenue.mFRR,
        percentOfTotal: (capacityRevenue.mFRR / totalRevenue * 100).toFixed(1),
      },
      aFRR: {
        hours: aFRRHours,
        revenue: capacityRevenue.aFRR,
        percentOfTotal: (capacityRevenue.aFRR / totalRevenue * 100).toFixed(1),
      },
      FCR_D: {
        hours: 0,
        revenue: 0,
        percentOfTotal: '0',
      },
      energy: {
        hours: activationHours,
        MWh: activationMWh,
        revenue: energyRevenue,
        percentOfTotal: (energyRevenue / totalRevenue * 100).toFixed(1),
      },
    },
    uncertainty: {
      conservative: totalRevenue * BALANCE_MARKET_CONFIG.marketRisks.uncertaintyRange.conservative,
      expected: totalRevenue,
      optimistic: totalRevenue * BALANCE_MARKET_CONFIG.marketRisks.uncertaintyRange.optimistic,
    },
  };
};

/**
 * Formatér beløb til præsentation
 */
export const formatRevenue = (amount) => {
  if (amount >= 1000000) {
    return `${(amount / 1000000).toFixed(2)} M DKK`;
  }
  return `${(amount / 1000).toFixed(0)} k DKK`;
};

export default BALANCE_MARKET_CONFIG;